import { useCallback, useEffect, useRef, useState } from 'react';
import { data_from_server_post } from '@/shared/http/client';

const UNCHANGED_STATUS = {
    hasReference: false,
    datasetsChanged: false,
    benchmarksChanged: false,
    configChanged: false,
    teacherModelChanged: false,
    teacherPromptChanged: false,
    specificPromptChanged: false,
    changes: [],
    latestTrainingEventId: null,
    latestTrainingVersion: null,
};

/**
 * What changed since the task's latest training / re-evaluation run
 * (`POST task-change-status`) — drives the task page's primary button:
 * dataset changes → Retrain, benchmark/config changes → Revaluate,
 * nothing → Deploy.
 *
 * Detection is content-based on the backend (an edit that is reverted no
 * longer counts), so the hook only decides *when* to re-ask: Data Editor
 * mutations bump `refreshKey` (dataOverviewRefreshKey), uploads and Model
 * Configuration autosaves update the usecase fields below, and a training
 * start/completion flips `usecase.status`. Errors and disabled states fall
 * back to "unchanged" (Deploy stays primary).
 */
export function useTaskChangeStatus({ usecase, enabled = true, refreshKey = 0 }) {
    const [status, setStatus] = useState(UNCHANGED_STATUS);
    // Guards against out-of-order responses (rapid edits fire overlapping
    // requests) and drops responses that arrive after a disable/unmount.
    const requestIdRef = useRef(0);

    const clientId = usecase?.client_id;
    const usecaseId = usecase?._id;

    const refresh = useCallback(() => {
        if (!enabled || !clientId || !usecaseId) return;
        const requestId = ++requestIdRef.current;
        data_from_server_post(
            'task-change-status',
            { client_id: clientId, llm_usecase_id: usecaseId },
            (result) => {
                if (requestId !== requestIdRef.current) return;
                setStatus({
                    hasReference: Boolean(result?.has_reference),
                    datasetsChanged: Boolean(result?.datasets_changed),
                    benchmarksChanged: Boolean(result?.benchmarks_changed),
                    configChanged: Boolean(result?.config_changed),
                    teacherModelChanged: Boolean(result?.teacher_model_changed),
                    teacherPromptChanged: Boolean(result?.teacher_prompt_changed),
                    specificPromptChanged: Boolean(result?.specific_prompt_changed),
                    changes: Array.isArray(result?.changes) ? result.changes : [],
                    // Root training event of the latest version — the "from"
                    // side of the task page's Versions diff entry.
                    latestTrainingEventId: result?.latest_training_event_id || null,
                    latestTrainingVersion: result?.latest_training_version ?? null,
                });
            },
            () => {
                if (requestId !== requestIdRef.current) return;
                setStatus(UNCHANGED_STATUS);
            }
        );
    }, [enabled, clientId, usecaseId]);

    const datasetCount = (usecase?.datasets || []).length;
    const benchmarkCount = (usecase?.benchmarks || []).length;

    useEffect(() => {
        if (!enabled || !clientId || !usecaseId) {
            requestIdRef.current += 1;
            setStatus(UNCHANGED_STATUS);
            return;
        }
        refresh();
    }, [
        enabled,
        clientId,
        usecaseId,
        refresh,
        refreshKey,
        usecase?.status,
        usecase?.prompt,
        usecase?.teacher_system_prompt,
        usecase?.teacher_schema,
        usecase?.teacher_model,
        usecase?.teacher_provider,
        usecase?.comparison_mode,
        usecase?.comparison_mode_option,
        usecase?.generative_student_prompt,
        usecase?.generative_student_instructions,
        usecase?.include_instruction_in_training,
        datasetCount,
        benchmarkCount,
    ]);

    return { ...status, refresh };
}
